import React, { useState, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';
import { PolicyTemplate, DINEDetails, PolicyDetails } from '../../../types/policy';
import { Tooltip } from '../../common/Tooltip';

/**
 * Props interface for the DeploymentTab component
 * @property policy - The current policy template being edited
 * @property onUpdatePolicy - Callback for updating the entire policy object
 */
interface DeploymentTabProps {
  policy: PolicyTemplate;
  onUpdatePolicy: (updatedPolicy: PolicyTemplate) => void;
}

/**
 * Type guard to check if details are DINEDetails
 */
const isDINEDetails = (details: PolicyDetails | undefined): details is DINEDetails => {
  return details !== undefined && 'deployment' in details;
};

/**
 * Default skeleton template for new deployments
 */
const DEFAULT_DEPLOYMENT_TEMPLATE = {
  properties: {
    mode: 'Incremental',
    template: {
      "$schema": "https://schema.management.azure.com/schemas/2015-01-01/deploymentTemplate.json#",
      "contentVersion": "1.0.0.0",
      "parameters": {
        "name": {
          "type": "string"
        }
      },
      "resources": []
    },
    parameters: {
      "name": {
        "value": "[field('fullName')]"
      }
    }
  }
};

/**
 * DeploymentTab component for managing ARM template deployment configuration
 * 
 * This component provides a Monaco editor interface for defining ARM templates
 * used with the "deployIfNotExists" policy effect. It allows users to write
 * complete ARM templates that will be deployed when the policy triggers.
 */
export const DeploymentTab: React.FC<DeploymentTabProps> = ({
  policy,
  onUpdatePolicy
}) => {
  const [editorValue, setEditorValue] = useState<string>('');
  const [lastValidValue, setLastValidValue] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize editor value from policy
  useEffect(() => {
    const details = policy.properties.policyRule.then.details;
    
    if (isDINEDetails(details) && details.deployment) {
      const jsonValue = JSON.stringify(details.deployment, null, 2);
      setEditorValue(jsonValue);
      setLastValidValue(jsonValue);
    } else {
      const defaultValue = JSON.stringify(DEFAULT_DEPLOYMENT_TEMPLATE, null, 2);
      setEditorValue(defaultValue);
      setLastValidValue(defaultValue);
    }
    setParseError(null);
    setHasUnsavedChanges(false);
  }, [policy]);

  /**
   * Handles editor content changes - now allows free editing
   */
  const handleEditorChange = (value: string | undefined) => {
    if (value === undefined) return;
    
    setEditorValue(value);
    setHasUnsavedChanges(value !== lastValidValue);
    
    // Clear parse error when user starts typing
    if (parseError) {
      setParseError(null);
    }
  };

  /**
   * Validates and saves the current editor content
   */
  const handleSave = () => {
    try {
      const deploymentConfig = JSON.parse(editorValue);
      updatePolicyDeployment(deploymentConfig);
      setLastValidValue(editorValue);
      setHasUnsavedChanges(false);
      setParseError(null);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Invalid JSON syntax');
    }
  };

  /**
   * Handles editor blur - auto-save if valid JSON
   */
  const handleEditorBlur = () => {
    if (hasUnsavedChanges) {
      try {
        const deploymentConfig = JSON.parse(editorValue);
        updatePolicyDeployment(deploymentConfig);
        setLastValidValue(editorValue);
        setHasUnsavedChanges(false);
        setParseError(null);
      } catch (error) {
        // Don't auto-save on blur if JSON is invalid, just show the error
        setParseError(error instanceof Error ? error.message : 'Invalid JSON syntax');
      }
    }
  };

  /**
   * Updates the policy with new deployment configuration
   */
  const updatePolicyDeployment = (deploymentConfig: any) => {
    const details = policy.properties.policyRule.then.details;
    
    if (isDINEDetails(details)) {
      const updatedDetails: DINEDetails = {
        ...details,
        deployment: deploymentConfig
      };

      const updatedPolicy: PolicyTemplate = {
        ...policy,
        properties: {
          ...policy.properties,
          policyRule: {
            ...policy.properties.policyRule,
            then: {
              ...policy.properties.policyRule.then,
              details: updatedDetails
            }
          }
        }
      };

      onUpdatePolicy(updatedPolicy);
    }
  };

  /**
   * Resets the editor to default template
   */
  const handleResetToDefault = () => {
    const defaultJson = JSON.stringify(DEFAULT_DEPLOYMENT_TEMPLATE, null, 2);
    setEditorValue(defaultJson);
    setLastValidValue(defaultJson);
    setHasUnsavedChanges(false);
    setParseError(null);
    updatePolicyDeployment(DEFAULT_DEPLOYMENT_TEMPLATE);
  };

  /**
   * Discards unsaved changes
   */
  const handleDiscard = () => {
    setEditorValue(lastValidValue);
    setHasUnsavedChanges(false);
    setParseError(null);
  };

  return (
    <div className="flex flex-col w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm h-full">
      <div className="flex flex-col h-full">
        {/* Header with description and actions */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-6 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                ARM Template Deployment
              </h3>
              <Tooltip 
                content="Configure the ARM template deployment that will be triggered when the policy condition is met and the specified resource doesn't exist.

• properties.mode: Deployment mode ('Incremental' or 'Complete')
• properties.template: ARM template definition with $schema, contentVersion, parameters, and resources  
• properties.parameters: Parameter values to pass to the template
• Policy Functions: Use [field('name')], [field('fullName')], [resourceGroup().name], etc."
                position="right"
                maxWidth={500}
              />
            </div>
            
            <div className="flex items-center gap-2">
              {hasUnsavedChanges && (
                <>
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-md transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save
                  </button>
                  <button
                    onClick={handleDiscard}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 rounded-md transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Discard
                  </button>
                </>
              )}
              <button
                onClick={handleResetToDefault}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 rounded-md transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Define the ARM template that will be deployed when the policy triggers.
            </p>
            
            {hasUnsavedChanges && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 text-sm text-red-600 dark:text-red-400 font-medium bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Unsaved changes
              </div>
            )}
          </div>
        </div>

        {/* Error display */}
        {parseError && (
          <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex-shrink-0">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">JSON Syntax Error</h4>
                <p className="text-sm text-red-700 dark:text-red-300">{parseError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Monaco Editor - takes up remaining space */}
        <div className="flex-1 m-6 mt-4 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
          <Editor
            height="100%"
            defaultLanguage="json"
            value={editorValue}
            onChange={handleEditorChange}
            onMount={(editor, monaco) => {
              // Add blur event listener for auto-save
              editor.onDidBlurEditorText(handleEditorBlur);
              
              // Add keyboard shortcut for save (Ctrl+S)
              editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
                handleSave();
              });
            }}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              renderWhitespace: 'selection',
              folding: true,
              wordWrap: 'on',
              automaticLayout: true,
              scrollBeyondLastLine: false,
              formatOnPaste: true,
              formatOnType: true,
              bracketPairColorization: { enabled: true },
              tabSize: 2,
              insertSpaces: true,
              detectIndentation: false
            }}
          />
        </div>
      </div>
    </div>
  );
}; 